/**
 * ReleaseNotesAutoFeed — renders System Docs → Release Notes from the
 * build-time generated /app/frontend/src/data/releaseNotes.generated.json
 * (sourced from /app/memory/CHANGELOG.md).
 *
 * Why: hand-curated Release Notes JSX drifted out of sync with the
 * changelog after every release. This component is a thin renderer
 * over the parsed entries so the engineer-facing changelog stays the
 * single source of truth.
 *
 * Lightweight markdown subset (kept tiny on purpose):
 *   - Paragraphs (blank-line separated)
 *   - **bold**, *italic*, `inline code`
 *   - `- item` unordered lists, `1. item` ordered lists
 *   - `> quoted` blockquotes
 *   - Inline links `[text](url)`
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Clock } from 'lucide-react';
import data from '../../data/releaseNotes.generated.json';

const BADGE_CLASSES = {
  teal: 'bg-teal-100 text-teal-800',
  purple: 'bg-purple-100 text-purple-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  emerald: 'bg-emerald-100 text-emerald-800',
  blue: 'bg-blue-100 text-blue-800',
  amber: 'bg-amber-100 text-amber-800',
  cyan: 'bg-cyan-100 text-cyan-800',
  rose: 'bg-rose-100 text-rose-800',
  orange: 'bg-orange-100 text-orange-800',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-800',
  slate: 'bg-slate-100 text-slate-800',
};

/**
 * Render inline-markdown tokens (bold/italic/code/link). Keep it
 * stateless — we walk left-to-right and emit React fragments. No
 * external dep so the bundle stays unchanged.
 */
function renderInline(text, keyPrefix) {
  const out = [];
  // Match the longest pattern first to avoid `*bold*` getting eaten by italic.
  const pattern =
    /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) {
      out.push(text.slice(last, m.index));
    }
    const tok = m[0];
    const k = `${keyPrefix}-i${i++}`;
    if (tok.startsWith('**')) {
      out.push(<strong key={k}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      out.push(
        <code key={k} className="bg-slate-100 text-slate-800 px-1 rounded text-[0.85em]">
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith('[')) {
      const lm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      if (lm) {
        out.push(
          <a
            key={k}
            href={lm[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-700 underline hover:text-emerald-900"
          >
            {lm[1]}
          </a>,
        );
      } else {
        out.push(tok);
      }
    } else if (tok.startsWith('*')) {
      out.push(<em key={k}>{tok.slice(1, -1)}</em>);
    } else {
      out.push(tok);
    }
    last = pattern.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * Parse a body string into block-level elements. We intentionally
 * keep this simple (matches what the `finish` step produces, no more).
 */
function renderBody(body, keyPrefix) {
  const lines = body.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Skip blank lines
    if (!line.trim()) { i++; continue; }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={`${keyPrefix}-ul-${i}`} className="list-disc pl-6 space-y-1.5 my-2">
          {items.map((it, idx) => (
            <li key={`${keyPrefix}-li-${i}-${idx}`}>{renderInline(it, `${keyPrefix}-li-${i}-${idx}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={`${keyPrefix}-ol-${i}`} className="list-decimal pl-6 space-y-1.5 my-2">
          {items.map((it, idx) => (
            <li key={`${keyPrefix}-oli-${i}-${idx}`}>{renderInline(it, `${keyPrefix}-oli-${i}-${idx}`)}</li>
          ))}
        </ol>,
      );
      continue;
    }

    // Blockquote
    if (/^\s*>\s+/.test(line)) {
      const quoted = [];
      while (i < lines.length && /^\s*>\s+/.test(lines[i])) {
        quoted.push(lines[i].replace(/^\s*>\s+/, ''));
        i++;
      }
      blocks.push(
        <blockquote
          key={`${keyPrefix}-bq-${i}`}
          className="border-l-4 border-slate-300 pl-3 py-1 italic text-slate-600 my-2"
        >
          {renderInline(quoted.join(' '), `${keyPrefix}-bq-${i}`)}
        </blockquote>,
      );
      continue;
    }

    // Regular paragraph — collect until blank line / structural break.
    const para = [line];
    i++;
    while (
      i < lines.length
      && lines[i].trim()
      && !/^\s*[-*]\s+/.test(lines[i])
      && !/^\s*\d+\.\s+/.test(lines[i])
      && !/^\s*>\s+/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={`${keyPrefix}-p-${i}`} className="my-2">
        {renderInline(para.join(' '), `${keyPrefix}-p-${i}`)}
      </p>,
    );
  }

  return blocks;
}

export default function ReleaseNotesAutoFeed() {
  const entries = data?.entries || [];
  return (
    <div className="space-y-6" data-testid="release-notes-auto-feed">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle
            className="text-xl font-semibold text-slate-900 flex items-center gap-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            <Clock className="w-6 h-6 text-emerald-600" />
            Recent Release Notes
          </CardTitle>
          {data?.generatedAt && (
            <p className="text-[10px] text-slate-400 -mt-1">
              Auto-generated from <code className="bg-slate-100 px-1 rounded">/app/memory/CHANGELOG.md</code> at{' '}
              {new Date(data.generatedAt).toLocaleString()} · {entries.length} releases
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-6 text-sm text-slate-700 leading-relaxed">
          {entries.length === 0 ? (
            <p className="text-slate-500 italic">
              No release notes have been generated yet. Run
              <code className="bg-slate-100 px-1 rounded mx-1">yarn generate-release-notes</code>
              to populate.
            </p>
          ) : (
            entries.map((entry, idx) => (
              <div
                key={entry.version}
                className="pb-4 border-b border-slate-100 last:border-b-0"
                data-testid={`release-note-${entry.version}`}
              >
                <Badge className={BADGE_CLASSES[entry.color] || BADGE_CLASSES.slate}>
                  {entry.version} — {entry.date}
                </Badge>
                {entry.title && (
                  <p className="font-semibold text-slate-900 mt-2 mb-1">
                    {entry.title}
                  </p>
                )}
                <div className="text-sm text-slate-700">
                  {renderBody(entry.body || '', `e${idx}`)}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
